/**
 * Plain words for an audit row. The API names an action after its code
 * (`organization.updateOrganization`): the handler's leading verb gives the
 * label, the HTTP method when it starts with none of ours.
 */
export const VERBS = [
  'activate',
  'add',
  'analyze',
  'apply',
  'archive',
  'book',
  'cancel',
  'confirm',
  'create',
  'decide',
  'delete',
  'discard',
  'duplicate',
  'email',
  'forgot',
  'impersonate',
  'login',
  'pause',
  'propose',
  'purge',
  'remove',
  'resend',
  'reset',
  'resume',
  'resync',
  'retry',
  'rotate',
  'save',
  'send',
  'submit',
  'sync',
  'toggle',
  'unarchive',
  'update',
  'validate',
];

const METHOD_VERBS = {
  POST: 'create',
  PUT: 'update',
  PATCH: 'update',
  DELETE: 'delete',
};

export function actionVerb({ action, httpMethod }) {
  const handler = action?.split('.').pop() ?? '';
  const first = handler.match(/^[a-z]+/)?.[0];
  return VERBS.includes(first) ? first : (METHOD_VERBS[httpMethod] ?? 'update');
}

/** organization-api-keys -> Organization api key, for types without a label. */
export function humanizeType(type) {
  const words = type.replace(/-/g, ' ').replace(/ies$/, 'y').replace(/s$/, '');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A booking's state change, logged whatever made it (KD-3937): a supplier's
 * or Koob's from the booking's history, a DMC's answer from its request.
 */
export const isStateChange = (row) =>
  row.action === 'booking.stateChanged' || Boolean(row.details?.to);

/** A booking that did not go through: the API says why. */
export const isFailedBooking = (row) => Boolean(row.details?.failureReason);

/** What a pricing summary prices: `trip` or `booking`. */
export const pricingTarget = (row) => row.details?.target ?? null;

/** "Amber Kampot #12" when the API found a name, else "#12". */
const named = ({ id, name }) => (name ? `${name} #${id}` : `#${id}`);

/** The bookings a booking replaced, by hotel and id. */
export const replacedBookings = (row) =>
  (row.details?.replaced ?? []).map(named);

/** A trip by its name, as everywhere else in the log, else "#4". */
const tripName = ({ id, name }) => name ?? `#${id}`;

/** The trip a booking was made from. */
export const bookingTrip = (row) =>
  row.details?.trip ? tripName(row.details.trip) : null;

/** The experiences a booking booked, by name. */
export const bookedExperiences = (row) =>
  (row.details?.booked ?? []).map(({ id, name }) => name ?? `#${id}`);

/** What a pricing summary prices: a booking by name and id, a trip by name. */
export const pricedName = (row) => {
  const priced = row.details?.priced;
  if (!priced) return null;
  return pricingTarget(row) === 'trip' ? tripName(priced) : named(priced);
};

/** The bookings a "Cancel all bookings" cancelled, by hotel and id. */
export const cancelledBookings = (row) =>
  (row.details?.cancelled ?? []).map(named);

/** The travelers a folder's traveler action names. */
export const travelerNames = (row) => row.details?.travelers ?? [];

/** The verb an action reads as: the API's when one call creates or updates. */
export const verbOf = (row) => row.details?.verb ?? actionVerb(row);

/** The sentence's verb; a state change has none: "Booking X Confirmed". */
export const actionLabel = (t, row) => {
  if (isStateChange(row)) return null;
  if (replacedBookings(row).length) return t('verbs.replace');
  if (pricingTarget(row)) return t(`pricingUpdated.${pricingTarget(row)}`);
  return t(`verbs.${verbOf(row)}`);
};

export const stateLabel = (t, state) => t(`states.${state}`);

export const resourceTypeLabel = (t, type) =>
  t(`resourceTypes.${type}`, { defaultValue: humanizeType(type) });

/**
 * "Easia Travel" when the API found a name, else "#12". A booking is named by
 * its hotel and id: two bookings of one hotel in a folder stay apart.
 */
export const resourceName = (row) => {
  if (!row.resourceName) return row.resourceId ? `#${row.resourceId}` : '';
  return row.resourceType === 'bookings'
    ? named({ id: row.resourceId, name: row.resourceName })
    : row.resourceName;
};

const VERB_TONES = {
  add: 'create',
  create: 'create',
  duplicate: 'create',
  book: 'create',
  cancel: 'delete',
  delete: 'delete',
  discard: 'delete',
  purge: 'delete',
  remove: 'delete',
  forgot: 'access',
  impersonate: 'access',
  login: 'access',
  email: 'send',
  resend: 'send',
  send: 'send',
  archive: 'archive',
  unarchive: 'unarchive',
};

const STATE_TONES = {
  confirmed: 'create',
  pre_confirmed: 'create',
  canceled: 'delete',
  refused: 'delete',
};

/**
 * create, delete, access, send, archive, unarchive or edit: picks the
 * timeline dot's icon and color. A state change takes its new state's.
 */
export const verbTone = (row) =>
  isStateChange(row)
    ? (STATE_TONES[row.details?.to] ?? 'edit')
    : (VERB_TONES[verbOf(row)] ?? 'edit');

/** Local calendar day of a date, as YYYY-MM-DD. */
export const dayKey = (value) => {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Rows (newest first) as [{ day, rows }], one group per local day, in order. */
export function groupByDay(rows) {
  const groups = [];
  rows.forEach((row) => {
    const day = dayKey(row.occurredAt);
    const last = groups[groups.length - 1];
    if (last?.day === day) last.rows.push(row);
    else groups.push({ day, rows: [row] });
  });
  return groups;
}
